import * as React from 'react';
import MUIChip, { ChipProps } from '@mui/material/Chip';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';

export type PillProps = BpDesignSystem.Props<
  Omit<ChipProps, 'variant' | 'size' | 'color'> & {
    text: React.ReactNode;
    variant?: 'black' | 'grey' | 'orange' | 'green' | 'blue' | 'red';
    size?: 'small' | 'medium';
    primary?: boolean;
    leftIcon?: JSX.Element;
    rightIcon?: JSX.Element;
    onRightIconClick?(): void;
    clickable?: boolean;
  }
>;

export const Pill: React.FC<PillProps> = ({
  text,
  variant,
  primary = false,
  leftIcon,
  size = 'medium',
  clickable = false,
  rightIcon,
  onClick,
  onRightIconClick,
  dataTest,
  style,
  className,
  ...props
}) => (
  <StyledChip
    data-test={dataTest}
    className={`pill-${variant} ${primary ? 'pill-dark' : 'pill-light'} ${className ?? ''}`}
    icon={leftIcon}
    label={text}
    size={size}
    style={style}
    onDelete={onRightIconClick}
    clickable={clickable}
    onClick={onClick}
    deleteIcon={rightIcon}
    {...props}
  />
);

const StyledChip = styled(MUIChip)`
  &.MuiChip-root {
    color: ${({ theme }) => theme.tokens.colorOnPrimary};
    radius: ${({ theme }) => theme.tokens.radiusMax};;
    border: 1px solid transparent;
    height: ${({ size }) => (size === 'small' ? '16px' : '20px')};
    font-weight: 500;
    .MuiChip-deleteIcon:hover {
      color: ${({ theme }) => theme.tokens.colorInverseOnSurface};
    }

    padding-inline: 0;
  }

  .MuiChip-label {
    font-size: 12px;
    padding: ${({ size }) => (size === 'small' ? '2px 4px' : '2px 8px')};
    line-height: 12px;
  }

  .MuiChip-icon {
    width: 16px;
    height: 16px;
    font-size: 16px;
    border-radius: 5px;
    margin: 0 2px;
  }

  .MuiChip-deleteIcon {
    width: 16px;
    height: 16px;
    font-size: 16px;
    border-radius: 5px;
    margin: 0 2px;
  }

  .MuiChip-labelSmall {
    font-size: 10px;
    line-height: 10px;
  }

  .MuiChip-iconSmall {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.tokens.colorOnPrimary};
  }

  .MuiChip-deleteIconSmall {
    width: 14px;
    height: 14px;
    padding: 0;
  }



  &.pill-black.pill-dark {
    background-color: ${({ theme }) => theme.tokens.colorInverseSurface};
    color: ${({ theme }) => theme.tokens.colorInverseOnSurface};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorInverseOnSurface};
    }
  }

  &.pill-grey.pill-dark {
    background-color: ${({ theme }) => theme.tokens.paletteNeutralVariant30};
    color:${({ theme }) => theme.tokens.colorInverseOnSurface};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorInverseOnSurface};
    }
  }

  &.pill-orange.pill-dark {
    background-color: ${({ theme }) => theme.tokens.colorWarning};
    color: ${({ theme }) => theme.tokens.colorOnWarning};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorOnWarning};
    }
  }

  &.pill-green.pill-dark {
    background-color: ${({ theme }) => theme.tokens.colorSuccess};
    color: ${({ theme }) => theme.tokens.colorOnSuccess};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorOnSuccess};
    }
  }

  &.pill-blue.pill-dark {
    background-color: ${({ theme }) => theme.tokens.colorPrimary};
    color: ${({ theme }) => theme.tokens.colorOnPrimary};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorOnPrimary};
    }
  }

  &.pill-red.pill-dark {
    background-color: ${({ theme }) => theme.tokens.colorAlert};
    color: ${({ theme }) => theme.tokens.colorOnAlert};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorOnAlert};
    }
  }

  &.pill-light {
    color: ${({ theme }) => theme.tokens.paletteNeutralVariant20};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color: ${({ theme }) => theme.tokens.paletteNeutralVariant20};
      }
    }
    

  &.pill-black&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorInverseSurface};
    color: ${({ theme }) => theme.tokens.colorInverseOnSurface};
    border: 1px solid ${({ theme }) => theme.tokens.paletteNeutralVariant20};
    .MuiChip-icon, .MuiChip-deleteIcon {
      color:${({ theme }) => theme.tokens.colorInverseOnSurface};
    }
  }

  &.pill-grey&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorSurfaceVariant};
  }

  &.pill-orange&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorWarningContainer};
    color: ${({ theme }) => theme.tokens.colorOnWarningContainer};
  }

  &.pill-green&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorSuccessContainer};
    color: ${({ theme }) => theme.tokens.colorOnSuccessContainer};
  }

  &.pill-blue&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorPrimaryContainer};
    color: ${({ theme }) => theme.tokens.colorOnPrimaryContainer};
  }

  &.pill-red&.pill-light {
    background-color: ${({ theme }) => theme.tokens.colorAlertContainer};
    color: ${({ theme }) => theme.tokens.colorOnAlertContainer};
  }
`;
